/**
 * Referral Detail Modal Component
 *
 * Modal dialog for viewing and editing referral information.
 * Supports both view mode and edit mode with form validation.
 */

import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Input, Select, message } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { useReferralApi } from '../hooks/useReferralApi';
import type { Referral, ReferralListData, ReferralPayload } from '../hooks/useReferralApi';
import './ReferralDetailModal.css';

const { TextArea } = Input;
const { Option } = Select;

interface ReferralDetailModalProps {
  visible: boolean;
  referral: Referral | null;
  isCreating: boolean;
  listData: ReferralListData;
  onClose: () => void;
  onSave: (referral: Referral) => void;
}

const ReferralDetailModal: React.FC<ReferralDetailModalProps> = ({
  visible,
  referral,
  isCreating,
  listData,
  onClose,
  onSave
}) => {
  const [isEditing, setIsEditing] = useState(isCreating);
  const [form] = Form.useForm();
  const { fetchReferralDetails, saveReferral, isLoading } = useReferralApi();
  const [fullReferral, setFullReferral] = useState<Referral | null>(null);

  const normalizeTagId = (value: number | string | null | undefined): number | null => {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const normalized = Number(value);
    return Number.isFinite(normalized) ? normalized : null;
  };

  const toTagSelectValue = (value: number | string | null | undefined): string | undefined => {
    const normalized = normalizeTagId(value);
    return normalized === null ? undefined : String(normalized);
  };

  /**
   * Load full referral details when opening existing referral
   */
  useEffect(() => {
    if (visible && referral && !isCreating) {
      loadReferralDetails(referral.id);
    } else if (visible && isCreating) {
      setIsEditing(true);
      setFullReferral(null);
      form.resetFields();
    }
  }, [visible, referral, isCreating]);

  /**
   * Load complete referral data from API
   */
  const loadReferralDetails = async (referralId: number) => {
    try {
      const data = await fetchReferralDetails(referralId);
      const normalizedTagId = normalizeTagId(data.referral_tag_id);

      // Get tag information
      const tag = normalizedTagId !== null
        ? listData.referral_tags.find(t => t.id === normalizedTagId)
        : undefined;

      // Format display facilities
      const displayFacilities = Array.isArray(data.facilities)
        ? data.facilities.map((f: any) => typeof f === 'string' ? f : f.name).join(', ')
        : '';

      // Extract facility IDs
      const idFacilities = Array.isArray(data.facilities)
        ? data.facilities.map((f: any) => typeof f === 'object' ? f.id : null).filter(Boolean)
        : [];

      setFullReferral({
        ...data,
        referral_tag_id: normalizedTagId,
        tag,
        display_facilities: displayFacilities,
        id_facilities: idFacilities as number[]
      });

      // Populate form with referral data
      form.setFieldsValue({
        ...data,
        referral_tag_id: toTagSelectValue(normalizedTagId),
        id_facilities: idFacilities
      });
    } catch (error) {
      message.error('Failed to load referral details');
    }
  };

  /**
   * Check if referral has contact info
   */
  const hasContactInfo = () => {
    if (!fullReferral) return false;
    return !!(fullReferral.contact_email || fullReferral.contact_phone || fullReferral.website_url);
  };

  /**
   * Handle edit button click
   */
  const handleEdit = () => {
    setIsEditing(true);
  };

  /**
   * Handle save
   */
  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const payload: ReferralPayload = {
        id: isCreating ? -1 : fullReferral?.id,
        name: values.name,
        specialty: values.specialty,
        notes: values.notes,
        contact_phone: values.contact_phone,
        contact_email: values.contact_email,
        website_url: values.website_url,
        address_line_1: values.address_line_1,
        address_line_2: values.address_line_2,
        address_city: values.address_city,
        address_zip: values.address_zip,
        address_state: values.address_state,
        facilities: values.id_facilities || [],
        referral_tag_id: normalizeTagId(values.referral_tag_id)
      };

      const savedReferral = await saveReferral(payload);
      message.success('Referral saved successfully');

      onSave(savedReferral);
      setIsEditing(false);
    } catch (error) {
      if (error instanceof Error && error.message !== 'Validation failed') {
        message.error('Failed to save referral');
      }
    }
  };

  /**
   * Handle cancel
   */
  const handleCancel = () => {
    if (isCreating) {
      onClose();
    } else {
      setIsEditing(false);
      // Restore original values
      if (fullReferral) {
        form.setFieldsValue(fullReferral);
      }
    }
  };

  /**
   * Phone number validation
   */
  const validatePhone = (_: any, value: string) => {
    if (!value) return Promise.resolve();
    const phoneRegex = /^(\d{10}|\d{3}-\d{3}-\d{4})$/;
    if (phoneRegex.test(value)) {
      return Promise.resolve();
    }
    return Promise.reject(new Error('Phone must be 10 digits or in format: 999-999-9999'));
  };

  /**
   * Zip code validation
   */
  const validateZip = (_: any, value: string) => {
    if (!value) return Promise.resolve();
    const zipRegex = /^\d{5}(?:[-\s]\d{4})?$/;
    if (zipRegex.test(value)) {
      return Promise.resolve();
    }
    return Promise.reject(new Error('Invalid zip code format'));
  };

  /**
   * Email validation
   */
  const validateEmail = (_: any, value: string) => {
    if (!value) return Promise.resolve();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(value)) {
      return Promise.resolve();
    }
    return Promise.reject(new Error('Invalid email format'));
  };

  return (
    <Modal
      title={isCreating ? 'Add Referral' : fullReferral?.name || 'Referral Details'}
      open={visible}
      onCancel={onClose}
      width={700}
      footer={null}
      className="referral-detail-modal"
    >
      {!isEditing && fullReferral ? (
        // View Mode
        <div className="referral-view">
          <div className="referral-header">
            <h2>{fullReferral.name}</h2>
            <div className="referral-specialty">{fullReferral.specialty}</div>
          </div>

          {fullReferral.notes && (
            <div className="referral-section">
              <h3>Notes</h3>
              <div>{fullReferral.notes}</div>
            </div>
          )}

          {hasContactInfo() && (
            <div className="referral-section">
              <h3>Contact</h3>
              {fullReferral.contact_email && (
                <div>
                  <a href={`mailto:${fullReferral.contact_email}`}>
                    {fullReferral.contact_email}
                  </a>
                </div>
              )}
              {fullReferral.website_url && (
                <div>
                  <a href={fullReferral.website_url} target="_blank" rel="noopener noreferrer">
                    {fullReferral.website_url}
                  </a>
                </div>
              )}
              {fullReferral.contact_phone && (
                <div>
                  <a href={`tel:${fullReferral.contact_phone}`}>
                    {fullReferral.contact_phone}
                  </a>
                </div>
              )}
            </div>
          )}

          <div className="referral-section">
            <h3>Location{(fullReferral.facilities?.length || 0) > 1 ? 's' : ''}</h3>
            <div>{fullReferral.display_facilities}</div>
          </div>

          {(fullReferral.address_line_1 || fullReferral.address_city) && (
            <div className="referral-section">
              <h3>Address</h3>
              {fullReferral.address_line_1 && (
                <div>
                  {fullReferral.address_line_1}
                  {fullReferral.address_line_2 && `, ${fullReferral.address_line_2}`}
                </div>
              )}
              {fullReferral.address_city && (
                <div>
                  {fullReferral.address_city}
                  {fullReferral.address_state && `, ${fullReferral.address_state}`}
                </div>
              )}
              {fullReferral.address_zip && <div>{fullReferral.address_zip}</div>}
            </div>
          )}

          <div className="referral-section">
            <h3>Tag</h3>
            <div>{fullReferral.tag?.name || 'None'}</div>
          </div>

          <div className="referral-actions">
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={handleEdit}
            >
              Edit Referral
            </Button>
          </div>
        </div>
      ) : (
        // Edit Mode
        <div className="referral-editor">
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              id_facilities: [],
              referral_tag_id: undefined
            }}
          >
            <Form.Item
              label="Name"
              name="name"
              rules={[
                { required: true, message: 'Name is required' },
                { max: 30, message: 'Name must be 30 characters or less' }
              ]}
            >
              <Input placeholder="Enter referral name" />
            </Form.Item>

            <Form.Item
              label="Specialty"
              name="specialty"
              rules={[
                { required: true, message: 'Specialty is required' },
                { max: 30, message: 'Specialty must be 30 characters or less' }
              ]}
            >
              <Select
                showSearch
                placeholder="Enter or select specialty"
                allowClear
                optionFilterProp="children"
                filterOption={(input, option) =>
                  String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                }
                defaultActiveFirstOption
                getPopupContainer={(trigger) => trigger.parentElement || document.body}
              >
                {listData.specialties.map((specialty) => (
                  <Option key={specialty} value={specialty}>
                    {specialty}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Notes" name="notes">
              <TextArea rows={3} placeholder="Enter notes" />
            </Form.Item>

            <Form.Item
              label="Phone"
              name="contact_phone"
              rules={[
                { max: 25, message: 'Phone must be 25 characters or less' },
                { validator: validatePhone }
              ]}
            >
              <Input placeholder="999-999-9999" />
            </Form.Item>

            <Form.Item
              label="Email"
              name="contact_email"
              rules={[{ validator: validateEmail }]}
            >
              <Input type="email" placeholder="email@example.com" />
            </Form.Item>

            <Form.Item label="Website" name="website_url">
              <Input placeholder="https://example.com" />
            </Form.Item>

            <Form.Item
              label="Address Line 1"
              name="address_line_1"
              rules={[{ max: 100, message: 'Address must be 100 characters or less' }]}
            >
              <Input placeholder="Street address" />
            </Form.Item>

            <Form.Item
              label="Address Line 2"
              name="address_line_2"
              rules={[{ max: 100, message: 'Address must be 100 characters or less' }]}
            >
              <Input placeholder="Apt, suite, etc." />
            </Form.Item>

            <Form.Item
              label="City"
              name="address_city"
              rules={[{ max: 50, message: 'City must be 50 characters or less' }]}
            >
              <Input placeholder="City" />
            </Form.Item>

            <Form.Item
              label="Zip"
              name="address_zip"
              rules={[
                { max: 10, message: 'Zip must be 10 characters or less' },
                { validator: validateZip }
              ]}
            >
              <Input placeholder="12345 or 12345-6789" />
            </Form.Item>

            <Form.Item label="State" name="address_state">
              <Select
                placeholder="Select state"
                allowClear
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                }
                getPopupContainer={(trigger) => trigger.parentElement || document.body}
              >
                {listData.states.map((state) => (
                  <Option key={state.value} value={state.value}>
                    {state.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Facilities"
              name="id_facilities"
              rules={[{ required: true, message: 'At least one facility is required' }]}
            >
              <Select
                mode="multiple"
                placeholder="Select facilities"
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                }
                defaultActiveFirstOption
                getPopupContainer={(trigger) => trigger.parentElement || document.body}
              >
                {listData.facilities.map((facility) => (
                  <Option key={facility.id} value={facility.id}>
                    {facility.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Tag" name="referral_tag_id">
              <Select
                placeholder="Select tag"
                allowClear
                onChange={(value) => {
                  form.setFieldValue('referral_tag_id', toTagSelectValue(value));
                }}
                getPopupContainer={(trigger) => trigger.parentElement || document.body}
              >
                {listData.referral_tags.map((tag) => (
                  <Option key={tag.id} value={String(tag.id)}>
                    {tag.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>

          <div className="referral-actions">
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={isLoading}
            >
              Save
            </Button>
            <Button
              icon={<CloseOutlined />}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ReferralDetailModal;
